import { MediaTable, MediaTableRef } from "../MediaTable/MediaTable";
import { FilterBarButtonWithModal, SearchBar, ExploreSideBarLayout } from "@kaltura/mediaspace-shared-search";
import {
    BaseEntryResultType,
    FilterValues,
    SearchAndSelectEntry,
    SearchAndSelectEntryList,
} from "@kaltura/mediaspace-shared-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { extractDefaultFilterValues, translate, usePostKmsData } from "@kaltura/mediaspace-shared-utils";
import { Stack } from "@kaltura/mediaspace-shared-styled";
import { GalleryInfo, SearchAndSelectTabProps } from "./SearchAndSelectTabProps";
import { AutoComplete } from "@kaltura/ds-react-components";

/**
 * Search and Select component - multi source tab.
 */
export const MultiSourceTab = ({
    categoryId,
    mediaContext,
    filters,
    filterMenuProps,
    filterValues: initialValues,
    dataUrl,
    pageSize,
    columns,
    actions,
    showExploreButton,
    onExplore,
    galleries = [],
    selectionMode,
    onSelectionChange,
    selectedItemsResetter,
    conditionalMessages,
    showBaseInfoOnlyInExploreBar,
    columnsWidth,
}: SearchAndSelectTabProps) => {
    const mediaTableRef = useRef<MediaTableRef | null>(null);

    const [exploreEntry, setExploreEntry] = useState<SearchAndSelectEntry>();
    const [exploreEntryIndex, setExploreEntryIndex] = useState<number>();
    const convertedExploreEntry = useMemo(
        () =>
            exploreEntry
            && ({
                object: exploreEntry.entry,
                itemsData: [],
            } as unknown as BaseEntryResultType),
        [exploreEntry]
    );

    useEffect(() => {
        if (exploreEntry === undefined && exploreEntryIndex !== undefined) {
            mediaTableRef.current?.focusExploreButton(exploreEntryIndex);
        }
    }, [exploreEntry, exploreEntryIndex]);

    const [currentGallery, setCurrentGallery] = useState<GalleryInfo>(() => {
        // find in galleries one where id === categoryId
        const gallery = galleries.find((gallery) => gallery.id === categoryId);
        return gallery || galleries[0];
    });

    const getAutocompleteOptionLabel = useCallback((option: GalleryInfo) => option.name, []);
    const isAutocompleteOptionEqualToValue = useCallback(
        (option: GalleryInfo, value: GalleryInfo) => option.id === value.id,
        []
    );

    const [searchMediaUrl, setSearchMediaUrl] = useState<string>(() =>
        dataUrl.replace("[CATID]", String(currentGallery.id))
    );

    const [page, setPage] = useState(1);

    // filter values as the user set them (+ defaults)
    const defaultFilterValues = useMemo(
        () => (filters ? { ...extractDefaultFilterValues(filters), ...initialValues } : {}),
        [filters, initialValues]
    );
    const [filterValues, setFilterValues] = useState<Record<string, any>>(defaultFilterValues);

    const searchData = useMemo(() => ({ ...filterValues, page }), [filterValues, page]);
    const [loadingState, response] = usePostKmsData<SearchAndSelectEntryList>(searchMediaUrl, searchData);

    const handleFilterChange = (values: Partial<FilterValues>) => {
        setFilterValues((filterValues) => ({ ...filterValues, ...values }));
        setPage(1);

        setExploreEntry(undefined);
        setExploreEntryIndex(undefined);
        onSelectionChange?.([], currentGallery.id);
    };
    const handleKeywordSearchSubmit = (value: string) => handleFilterChange({ keyword: value });

    const isKeywordSearch = !!filterValues["keyword"];
    const sortBy = filterValues["sortBy"] ?? "relevance";
    const defaultSortBy = isKeywordSearch ? "relevance" : defaultFilterValues["sortBy"] ?? "relevance";
    const handleSortByChange = (sortBy: string) => handleFilterChange({ sortBy });

    // handle gallery selection changed
    useEffect(() => {
        setSearchMediaUrl(dataUrl.replace("[CATID]", String(currentGallery.id)));
        setPage(1);
        setFilterValues(defaultFilterValues);
        setExploreEntry(undefined);
        onSelectionChange?.([], currentGallery.id);
    }, [defaultFilterValues, dataUrl, currentGallery, onSelectionChange]);

    return (
        <ExploreSideBarLayout
            searchResult={convertedExploreEntry}
            filterValues={filterValues}
            categoryId={categoryId}
            mediaContext={mediaContext}
            showPlayer={true}
            showBaseInfoOnly={showBaseInfoOnlyInExploreBar}
            callToActionComponent={exploreEntry && actions && actions(exploreEntry, true, mediaContext)}
            onClose={() => setExploreEntry(undefined)}
        >
            <Stack direction={"column"} gap={3}>
                <div style={{ display: "flex", flexDirection: "row", gap: 8 }}>
                    <AutoComplete
                        options={galleries}
                        autoFocusInput={true}
                        getOptionLabel={getAutocompleteOptionLabel}
                        isOptionEqualToValue={isAutocompleteOptionEqualToValue}
                        multiple={false}
                        value={currentGallery}
                        onChange={(event, value) => {
                            setCurrentGallery(value);
                            onSelectionChange?.([], value.id);
                        }}
                        aria-label={translate("Select course")}
                    />
                    <SearchBar
                        leftButtons={
                            filters
                                ? [
                                      <FilterBarButtonWithModal
                                          filters={filters}
                                          values={filterValues}
                                          onChange={handleFilterChange}
                                      />,
                                  ]
                                : []
                        }
                        keywordSearchProps={{
                            onSubmit: handleKeywordSearchSubmit,
                            value: filterValues["keyword"] as string,
                            placeholder: translate("Search"),
                            ariaLabel: translate("Search"),
                        }}
                        filterMenuProps={
                            filterMenuProps && {
                                ...filterMenuProps,
                                values: filterValues,
                                onChange: handleFilterChange,
                            }
                        }
                    />
                </div>
                <MediaTable
                    ref={mediaTableRef}
                    entries={response?.objects}
                    totalCount={response?.totalCount ?? 0}
                    isLoading={loadingState === "loading"}
                    isError={loadingState === "error"}
                    pageSize={pageSize}
                    page={page}
                    onPageChange={setPage}
                    columns={columns}
                    columnsWidth={columnsWidth}
                    actions={actions ? (entry) => actions(entry, false, mediaContext) : undefined}
                    showExploreButton={showExploreButton}
                    onExplore={(entry, index) => {
                        setExploreEntry(entry);
                        setExploreEntryIndex(index);
                        onExplore?.(entry.entry.id);
                    }}
                    sortBy={sortBy}
                    defaultSortBy={defaultSortBy}
                    onSortByChange={handleSortByChange}
                    isKeywordSearch={isKeywordSearch}
                    selectionMode={selectionMode}
                    onSelectionChange={
                        onSelectionChange ? (selection) => onSelectionChange(selection, currentGallery.id) : undefined
                    }
                    selectedItemsResetIndicator={selectedItemsResetter}
                    conditionalMessages={conditionalMessages}
                />
            </Stack>
        </ExploreSideBarLayout>
    );
};
